import Head from 'next/head'
import { useRouter } from 'next/router'
import { useI18nSafe } from '../context/I18nContext'
import { canonicalizePath } from '../lib/localizedRoutes'
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  absoluteUrl,
  buildPageTitle,
  buildPageKeywords,
  getDefaultDescription,
  getLocaleUrls,
  safeJsonLd,
} from '../lib/seo'

export default function Seo({
  title = '',
  description = '',
  keywords = [],
  path = '',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noIndex = false,
  jsonLd = [],
}) {
  const router = useRouter()
  const { language } = useI18nSafe()
  const currentPath = String(path || router.asPath || '/')
  const routeFamily = canonicalizePath(currentPath) || '/'
  const localeUrls = getLocaleUrls(currentPath)
  const resolvedTitle = buildPageTitle(title)
  const resolvedDescription = String(description || '').trim() || getDefaultDescription(language)
  const resolvedKeywords = buildPageKeywords(title, keywords, language)
  const resolvedImage = absoluteUrl(image)
  const robotsValue = noIndex ? 'noindex,nofollow,noarchive' : 'index,follow'
  const jsonLdItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []
  const ogLocale = String(language || '').toLowerCase() === 'fr' ? 'fr_FR' : 'en_US'

  return (
    <Head>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={resolvedKeywords.join(', ')} />
      <meta name="robots" content={robotsValue} />
      <meta name="googlebot" content={robotsValue} />
      <link rel="canonical" href={localeUrls.currentUrl} />
      <link rel="alternate" hrefLang="en" href={localeUrls.enUrl} />
      <link rel="alternate" hrefLang="fr" href={localeUrls.frUrl} />
      <link rel="alternate" hrefLang="x-default" href={localeUrls.enUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={localeUrls.currentUrl} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:locale" content={ogLocale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      {jsonLdItems.map((item, index) => (
        <script
          // JSON-LD is intentionally injected raw for search engines.
          key={`${routeFamily}-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(item) }}
        />
      ))}
    </Head>
  )
}
